import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';

let ffmpeg: FFmpeg | null = null;

// Initialize FFmpeg
const initFFmpeg = async () => {
  if (!ffmpeg) {
    ffmpeg = new FFmpeg();
  }
  
  if (!ffmpeg.loaded) {
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
  }
  
  return ffmpeg;
};

// Generate thumbnail from video file
export const generateVideoThumbnail = async (
  videoFile: File,
  timeInSeconds: number = 1
): Promise<string> => {
  try {
    const ffmpegInstance = await initFFmpeg();
    
    // Write video file to FFmpeg filesystem
    const videoName = `input_${Date.now()}.${videoFile.name.split('.').pop()}`;
    const thumbnailName = `thumbnail_${Date.now()}.jpg`;
    
    const videoData = new Uint8Array(await videoFile.arrayBuffer());
    await ffmpegInstance.writeFile(videoName, videoData);
    
    // Generate thumbnail at specified time
    await ffmpegInstance.exec([
      '-i', videoName,
      '-ss', timeInSeconds.toString(),
      '-vframes', '1',
      '-vf', 'scale=320:568:force_original_aspect_ratio=decrease,pad=320:568:(ow-iw)/2:(oh-ih)/2',
      '-q:v', '2',
      thumbnailName
    ]);
    
    // Read the generated thumbnail
    const data = await ffmpegInstance.readFile(thumbnailName);
    
    // Clean up files
    await ffmpegInstance.deleteFile(videoName);
    await ffmpegInstance.deleteFile(thumbnailName);
    
    // Convert to blob URL
    const blob = new Blob([data], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
    
  } catch (error) {
    console.error('Error generating video thumbnail:', error);
    throw error;
  }
};

// Generate multiple thumbnails for video scrubbing
export const generateVideoThumbnails = async (
  videoFile: File,
  count: number = 5
): Promise<string[]> => {
  try {
    const ffmpegInstance = await initFFmpeg();
    
    // Get video duration first
    const videoName = `input_${Date.now()}.${videoFile.name.split('.').pop()}`;
    const videoData = new Uint8Array(await videoFile.arrayBuffer());
    await ffmpegInstance.writeFile(videoName, videoData);
    
    const thumbnails: string[] = [];
    
    // Generate thumbnails at different time intervals
    for (let i = 0; i < count; i++) {
      const timePercent = (i + 1) / (count + 1); // Avoid start and end
      const thumbnailName = `thumb_${i}_${Date.now()}.jpg`;
      
      await ffmpegInstance.exec([
        '-i', videoName,
        '-vf', `select=gte(t\\,${timePercent}*duration),scale=160:284:force_original_aspect_ratio=decrease,pad=160:284:(ow-iw)/2:(oh-ih)/2`,
        '-vframes', '1',
        '-q:v', '3',
        thumbnailName
      ]);
      
      const data = await ffmpegInstance.readFile(thumbnailName);
      const blob = new Blob([data], { type: 'image/jpeg' });
      thumbnails.push(URL.createObjectURL(blob));
      
      // Clean up individual thumbnail
      await ffmpegInstance.deleteFile(thumbnailName);
    }
    
    // Clean up video file
    await ffmpegInstance.deleteFile(videoName);
    
    return thumbnails;
    
  } catch (error) {
    console.error('Error generating video thumbnails:', error);
    throw error;
  }
};

// Generate thumbnail from video URL
export const generateThumbnailFromUrl = async (
  videoUrl: string,
  timeInSeconds: number = 1
): Promise<string> => {
  try {
    const ffmpegInstance = await initFFmpeg();
    
    const videoName = `input_${Date.now()}.mp4`;
    const thumbnailName = `thumbnail_${Date.now()}.jpg`;
    
    // Fetch video from URL
    const response = await fetch(videoUrl);
    const videoData = new Uint8Array(await response.arrayBuffer());
    await ffmpegInstance.writeFile(videoName, videoData);
    
    // Generate thumbnail
    await ffmpegInstance.exec([
      '-i', videoName,
      '-ss', timeInSeconds.toString(),
      '-vframes', '1',
      '-vf', 'scale=320:568:force_original_aspect_ratio=decrease,pad=320:568:(ow-iw)/2:(oh-ih)/2',
      '-q:v', '2',
      thumbnailName
    ]);
    
    const data = await ffmpegInstance.readFile(thumbnailName);
    
    // Clean up
    await ffmpegInstance.deleteFile(videoName);
    await ffmpegInstance.deleteFile(thumbnailName);
    
    const blob = new Blob([data], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
    
  } catch (error) {
    console.error('Error generating thumbnail from URL:', error);
    throw error;
  }
};

// Cleanup blob URLs to prevent memory leaks
export const cleanupThumbnails = (thumbnailUrls: string[]) => {
  thumbnailUrls.forEach(url => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};
